import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { UserRole } from '../types/database';
import type { User as SupabaseUser, Session as SupabaseSession } from '@supabase/supabase-js';

interface Profile {
  id: string;
  role: UserRole;
  fullName?: string;
  phone?: string;
  companyId?: string;
  departmentId?: string;
  isActive?: boolean;
}

interface User {
  id: string;
  email?: string;
  name?: string;
}

interface Session {
  user: User;
  profile: Profile;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  profileReady: boolean;
  signIn: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ data?: any; error?: any }>;
  signOut: () => Promise<{ error?: any }>;
  hasRole: (roles: UserRole[]) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isEditor: boolean;
  isOrgAdmin: boolean;
  isDeptAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name: supabaseUser.user_metadata?.full_name || supabaseUser.email,
  };
}

function mapProfile(row: any): Profile {
  return {
    id: row.id,
    role: row.role as UserRole,
    fullName: row.full_name,
    phone: row.phone,
    companyId: row.company_id,
    departmentId: row.department_id,
    isActive: row.is_active,
  };
}

const PROFILE_CACHE_KEY = 'hn_cached_profile';

function getCachedProfile(): Profile | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Profile;
  } catch {
    return null;
  }
}

function setCachedProfile(profile: Profile | null) {
  try {
    if (profile) {
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(PROFILE_CACHE_KEY);
    }
  } catch { /* quota exceeded, ignore */ }
}

function buildProfileFromJwt(supabaseUser: SupabaseUser): Profile {
  return {
    id: supabaseUser.id,
    role: (supabaseUser.user_metadata?.role as UserRole) || 'counselor',
    fullName: supabaseUser.user_metadata?.full_name,
    isActive: true,
  };
}

async function fetchProfileOnce(userId: string): Promise<Profile | null> {
  const timeoutPromise = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), 6000)
  );

  const queryPromise = supabase
    .from('profiles')
    .select('id, role, full_name, phone, company_id, department_id, is_active')
    .eq('id', userId)
    .single()
    .then(({ data, error }) => {
      if (error || !data) return null;
      return mapProfile(data);
    })
    .catch(() => null);

  return Promise.race([queryPromise, timeoutPromise]);
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const result = await fetchProfileOnce(userId);
  if (result) return result;

  // Retry once after a short delay
  await new Promise(r => setTimeout(r, 500));
  return fetchProfileOnce(userId);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const cachedProfile = getCachedProfile();

  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(cachedProfile);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(!!cachedProfile);

  const lastValidProfileRef = useRef<Profile | null>(cachedProfile);

  const updateState = useCallback(async (supabaseUser: SupabaseUser | null) => {
    try {
      if (!supabaseUser) {
        lastValidProfileRef.current = null;
        setCachedProfile(null);
        setUser(null);
        setProfile(null);
        setSession(null);
        setProfileReady(true);
        setLoading(false);
        return;
      }

      const mappedUser = mapSupabaseUser(supabaseUser);
      setUser(mappedUser);

      // Use cached profile immediately if available for this user
      const cached = lastValidProfileRef.current;
      if (cached && cached.id === supabaseUser.id) {
        setProfile(cached);
        setSession({ user: mappedUser, profile: cached });
        setProfileReady(true);
      }

      // Always fetch fresh data from DB (updates cache when done)
      const userProfile = await fetchProfile(supabaseUser.id);

      if (userProfile) {
        lastValidProfileRef.current = userProfile;
        setCachedProfile(userProfile);
        setProfile(userProfile);
        setSession({ user: mappedUser, profile: userProfile });
      } else {
        // fetchProfile failed – use fallbacks in priority order:
        // 1. Previously cached profile (ref)
        // 2. JWT user_metadata (always available client-side)
        const fallback = lastValidProfileRef.current
          ?? buildProfileFromJwt(supabaseUser);

        lastValidProfileRef.current = fallback;
        setCachedProfile(fallback);
        setProfile(fallback);
        setSession({ user: mappedUser, profile: fallback });
      }
    } catch (err) {
      console.error('Auth: updateState failed', err);
    } finally {
      setProfileReady(true);
      setLoading(false);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let resolved = false;
    const isResetPasswordPage = window.location.pathname === '/reset-password';

    const resolve = async (user: SupabaseUser | null) => {
      if (resolved || !mounted) return;
      resolved = true;
      if (user && !isResetPasswordPage) {
        await updateState(user);
      } else {
        setLoading(false);
      }
    };

    // Primary: onAuthStateChange (Supabase recommended approach)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;

        if (event === 'PASSWORD_RECOVERY') {
          resolved = true;
          setLoading(false);
          return;
        }

        if (isResetPasswordPage && event !== 'SIGNED_OUT') {
          resolved = true;
          setLoading(false);
          return;
        }

        if (event === 'INITIAL_SESSION') {
          // First load - resolve with current session
          await resolve(currentSession?.user ?? null);
          return;
        }

        // Subsequent auth changes (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED)
        resolved = true; // allow re-processing
        await updateState(currentSession?.user ?? null);
      }
    );

    // Fallback: if onAuthStateChange doesn't fire within 3 seconds, use getSession
    const timeout = setTimeout(async () => {
      if (resolved || !mounted) return;
      console.warn('Auth: onAuthStateChange slow, falling back to getSession');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) await resolve(session?.user ?? null);
      } catch {
        if (mounted) resolve(null);
      }
    }, 3000);

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [updateState]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoading(false);
        return { data: null, error: { message: translateAuthError(error.message) } };
      }

      // Profile will be loaded by onAuthStateChange, but we also load it here
      // for immediate availability
      let userProfile: Profile | null = null;
      if (data.user) {
        const mappedUser = mapSupabaseUser(data.user);
        userProfile = await fetchProfile(data.user.id);

        setUser(mappedUser);
        if (userProfile) {
          lastValidProfileRef.current = userProfile;
          setCachedProfile(userProfile);
          setProfile(userProfile);
          setSession({ user: mappedUser, profile: userProfile });
        }
      }

      setLoading(false);
      return { data: { user: data.user, session: data.session, profile: userProfile }, error: null };
    } catch (err: any) {
      setLoading(false);
      return { data: null, error: { message: err.message || 'Innlogging feilet' } };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
        },
      });

      if (error) {
        setLoading(false);
        return { data: null, error: { message: translateAuthError(error.message) } };
      }

      setLoading(false);
      return { data: { user: data.user, session: data.session }, error: null };
    } catch (err: any) {
      setLoading(false);
      return { data: null, error: { message: err.message || 'Registrering feilet' } };
    }
  };

  const signOut = async () => {
    lastValidProfileRef.current = null;
    setCachedProfile(null);
    setUser(null);
    setProfile(null);
    setSession(null);
    setProfileReady(false);

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        return { error: { message: error.message } };
      }
      return { error: null };
    } catch (err: any) {
      // Even if signOut fails, state is already cleared
      return { error: { message: err.message } };
    }
  };

  const hasRole = (roles: UserRole[]): boolean => {
    if (!profile) return false;
    if (profile.role === 'superadmin') return true;
    return roles.includes(profile.role);
  };

  const isSuperAdmin = profile?.role === 'superadmin' || false;
  const isAdmin = isSuperAdmin;
  const isEditor = profile?.role === 'editor' || isSuperAdmin;
  const isOrgAdmin = profile?.role === 'org_admin' || isSuperAdmin;
  const isDeptAdmin = profile?.role === 'dept_admin' || isOrgAdmin || isSuperAdmin;

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    profileReady,
    signIn,
    signUp,
    signOut,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isEditor,
    isOrgAdmin,
    isDeptAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function translateAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) {
    return 'Feil e-post eller passord';
  }
  if (message.includes('Email not confirmed')) {
    return 'E-posten er ikke bekreftet. Sjekk innboksen din.';
  }
  if (message.includes('User already registered')) {
    return 'En bruker med denne e-posten finnes allerede';
  }
  if (message.includes('Password should be at least')) {
    return 'Passordet må være minst 8 tegn';
  }
  return message;
}
